import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ArrowLeft, X } from "lucide-react";
import type { HelpArticleContent, ContentBlock } from "@/types/helpArticle";
import { getVideoEmbedUrl } from "@/lib/videoEmbed";
interface ArticlePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  article: {
    title: string;
    category: string;
    subtitle?: string;
    content: HelpArticleContent;
    footer_content?: string;
  };
}

// Render a single content block
function renderBlock(block: ContentBlock, index: number) {
  switch (block.type) {
    case "paragraph":
      return (
        <p key={block.id} className="text-foreground/90 leading-relaxed mb-4">
          {block.content}
        </p>
      );

    case "heading":
      return (
        <h2
          key={block.id}
          className="text-xl font-semibold text-foreground mt-8 mb-4 first:mt-0"
        >
          {block.content}
        </h2>
      );

    case "callout":
      return (
        <div
          key={block.id}
          className="border-l-4 border-primary bg-primary/5 p-4 rounded-r-md my-6"
        >
          {block.title && (
            <p className="font-semibold text-primary mb-1">{block.title}</p>
          )}
          <p className="text-sm text-foreground">{block.content}</p>
        </div>
      );

    case "image":
      return (
        <figure key={block.id} className="my-6">
          <img
            src={block.url}
            alt={block.alt}
            className="rounded-lg max-w-full h-auto mx-auto"
          />
          {block.caption && (
            <figcaption className="text-sm text-muted-foreground text-center mt-2">
              {block.caption}
            </figcaption>
          )}
        </figure>
      );

    case "video": {
      const embedUrl = getVideoEmbedUrl(block.url);
      return (
        <figure key={block.id} className="my-6">
          {block.title && (
            <p className="text-sm font-medium mb-2">{block.title}</p>
          )}
          <div className="aspect-video rounded-lg overflow-hidden bg-muted">
            {embedUrl ? (
              <iframe
                src={embedUrl}
                title={block.title || "Embedded video"}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                Invalid video URL
              </div>
            )}
          </div>
        </figure>
      );
    }

    default:
      return null;
  }
}

export function ArticlePreviewModal({
  isOpen,
  onClose,
  article,
}: ArticlePreviewModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl h-[90vh] p-0 gap-0">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-lg font-semibold">
            Article Preview
          </DialogTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        </DialogHeader>

        {/* Preview Content */}
        <ScrollArea className="flex-1 h-full">
          <div className="p-6 md:p-8 max-w-3xl mx-auto">
            {/* Breadcrumb */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm text-muted-foreground">
                <li>
                  <span className="hover:text-primary cursor-not-allowed">
                    Help Center
                  </span>
                </li>
                <li>
                  <span className="mx-1">•</span>
                </li>
                <li>
                  <span className="hover:text-primary cursor-not-allowed">
                    {article.category || "Category"}
                  </span>
                </li>
              </ol>
            </nav>

            {/* Back Button */}
            <Button
              variant="ghost"
              size="sm"
              className="mb-4 -ml-2 gap-2 text-muted-foreground hover:text-foreground cursor-not-allowed"
              disabled
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Help Center
            </Button>

            {/* Article Header */}
            <header className="mb-8">
              <h1 className="text-3xl font-bold text-foreground mb-3">
                {article.title || "Untitled Article"}
              </h1>
              {article.subtitle && (
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {article.subtitle}
                </p>
              )}
            </header>

            {/* Article Content */}
            <article className="help-article prose prose-slate dark:prose-invert max-w-none">
              {article.content?.blocks?.length > 0 ? (
                article.content.blocks.map((block, index) =>
                  renderBlock(block, index)
                )
              ) : (
                <p className="text-muted-foreground italic">
                  No content blocks added yet.
                </p>
              )}
            </article>

            {/* Footer Content */}
            {article.footer_content && (
              <footer className="mt-8 pt-6 border-t">
                <div className="text-sm text-muted-foreground">
                  {article.footer_content}
                </div>
              </footer>
            )}

            {/* Help Footer Note */}
            <div className="mt-8 p-4 bg-muted/50 rounded-lg border">
              <p className="text-sm text-muted-foreground">
                Need more help? Contact support or visit the{" "}
                <span className="text-primary cursor-not-allowed">
                  Help Center
                </span>{" "}
                for additional resources.
              </p>
            </div>
          </div>
        </ScrollArea>

        {/* Preview Mode Banner */}
        <div className="px-6 py-3 border-t bg-muted/30 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            This is a preview. Some links are disabled.
          </p>
          <Button onClick={onClose} size="sm">
            Close Preview
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
